/**
 * Service de gestion d'erreur centralisé
 * Masque les détails techniques en production et fournit des messages conviviaux
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Messages d'erreur conviviaux pour l'utilisateur
 */
const USER_FRIENDLY_MESSAGES = {
    401: "Identifiants incorrects. Veuillez vérifier votre email et mot de passe.",
    403: "Vous n'avez pas les permissions nécessaires pour effectuer cette action.",
    404: "La ressource demandée n'a pas été trouvée.",
    422: "Les données fournies ne sont pas valides.",
    429: "Trop de tentatives. Veuillez réessayer dans quelques instants.",
    500: "Une erreur s'est produite. Veuillez réessayer plus tard.",
    503: "Le service est temporairement indisponible. Veuillez réessayer plus tard.",
    network: "Erreur de connexion. Veuillez vérifier votre connexion internet.",
    default: "Une erreur inattendue s'est produite. Veuillez réessayer."
};

/**
 * Formate une erreur HTTP pour l'affichage à l'utilisateur
 * @param {Error} error - L'objet erreur
 * @param {Object} options - Options de formatage
 * @returns {Object} - { type: 'danger'|'warning'|'info', message: string }
 */
export const formatHttpError = (error, options = {}) => {
    const {
        showValidationErrors = true,
        customMessages = {},
        fallbackMessage = USER_FRIENDLY_MESSAGES.default
    } = options;

    // Erreur réseau (pas de response)
    if (!error.response) {
        logError('Network Error', error);
        return {
            type: 'danger',
            message: customMessages.network || USER_FRIENDLY_MESSAGES.network
        };
    }

    const { status, data } = error.response;

    // Log détaillé uniquement en développement
    logError(`HTTP ${status} Error`, {
        url: error.config?.url,
        method: error.config?.method,
        status,
        data: isDevelopment ? data : '[Hidden in production]'
    });

    // Gestion des erreurs de validation (422)
    if (status === 422 && showValidationErrors && data?.errors) {
        const validationError = extractFirstValidationError(data);
        return {
            type: 'warning',
            message: validationError || customMessages[422] || USER_FRIENDLY_MESSAGES[422]
        };
    }

    // Message personnalisé par status code
    const message = customMessages[status] || USER_FRIENDLY_MESSAGES[status] || fallbackMessage;

    return {
        type: getAlertType(status),
        message
    };
};

/**
 * Extrait la première erreur de validation d'une réponse 422
 * @param {Object} data - Les données de réponse
 * @returns {string|null} - Le message d'erreur ou null
 */
const extractFirstValidationError = (data) => {
    if (!data || typeof data !== 'object') return null;

    // Format Laravel: { errors: { field: ['message'] } }
    if (data.errors && typeof data.errors === 'object') {
        const firstError = Object.values(data.errors)[0];
        return Array.isArray(firstError) ? firstError[0] : null;
    }

    // Format simple: { message: 'error' }
    if (data.message && typeof data.message === 'string') {
        return data.message;
    }

    return null;
};

/**
 * Détermine le type d'alerte basé sur le status HTTP
 * @param {number} status - Le code status HTTP
 * @returns {string} - 'danger'|'warning'|'info'
 */
const getAlertType = (status) => {
    if (status >= 500) return 'danger';
    if (status >= 400) return 'warning';
    return 'info';
};

/**
 * Log une erreur (uniquement en développement)
 * @param {string} context - Le contexte de l'erreur
 * @param {any} error - L'erreur à logger
 */
const logError = (context, error) => {
    if (isDevelopment && console && console.error) {
        console.error(`[${context}]`, error);
    }
};

/**
 * Gère les erreurs de formulaire
 * @param {Error} error - L'objet erreur
 * @param {Function} setAlert - Fonction pour définir l'alerte
 * @param {Object} options - Options de gestion
 */
export const handleFormError = (error, setAlert, options = {}) => {
    const formattedError = formatHttpError(error, options);
    setAlert(formattedError);
};

/**
 * Crée un gestionnaire d'erreur pour async/await
 * @param {Function} asyncFn - La fonction asynchrone
 * @returns {Function} - Fonction wrappée avec gestion d'erreur
 */
export const withErrorHandler = (asyncFn) => {
    return async (...args) => {
        try {
            return await asyncFn(...args);
        } catch (error) {
            logError('Async Operation Failed', error);
            throw error;
        }
    };
};

/**
 * Vérifie si une erreur est une erreur d'authentification
 * @param {Error} error - L'objet erreur
 * @returns {boolean}
 */
export const isAuthError = (error) => {
    return error?.response?.status === 401;
};

/**
 * Vérifie si une erreur est une erreur de permission
 * @param {Error} error - L'objet erreur
 * @returns {boolean}
 */
export const isPermissionError = (error) => {
    return error?.response?.status === 403;
};

/**
 * Extrait les données de réponse d'une erreur de manière sécurisée
 * @param {Error} error - L'objet erreur
 * @returns {Object|null}
 */
export const getErrorResponseData = (error) => {
    return error?.response?.data || null;
};

export default {
    formatHttpError,
    handleFormError,
    withErrorHandler,
    isAuthError,
    isPermissionError,
    getErrorResponseData
};
